/**
 * Los dos idiomas, y los dos ejes que NO hay que confundir.
 *
 * ── El idioma de quien trabaja ──────────────────────────────────────────────
 * El panel, el editor y los mensajes de error. Es una preferencia de la
 * PERSONA y viaja con ella a todos sus proyectos.
 *
 * ── El idioma del negocio ───────────────────────────────────────────────────
 * Los correos de aviso, la página pública de reservas, el checkout y las
 * políticas. Es una propiedad del PROYECTO, porque quien los lee es el cliente
 * de nuestro cliente. Sale de la región (`region.language`), no de aquí.
 *
 * Son dos cosas distintas y se decide cada una por su lado: una agencia de
 * Miami trabaja en inglés y monta el sitio de una taquería de Guadalajara, cuyo
 * comensal tiene que recibir su correo en español. Con un solo ajuste, uno de
 * los dos siempre está mal.
 */
export const LANGUAGES = ['es', 'en'];
/**
 * El de partida mientras no haya preferencia guardada.
 *
 * Español y no inglés porque es el idioma en que está escrito hoy el producto:
 * hasta que la traducción esté completa, caer al español enseña una pantalla
 * terminada en vez de una a medias. Se cambia el día que el inglés esté al día.
 */
export const DEFAULT_LANGUAGE = 'es';
export function isLanguage(value) {
    return typeof value === 'string' && LANGUAGES.includes(value);
}
/**
 * El idioma que pide el navegador, si sabemos hablarlo.
 *
 * Se mira la lista entera y no sólo `navigator.language`: quien tiene el
 * sistema en francés y el español de segundo prefiere español antes que el
 * idioma por defecto nuestro, y eso sólo se ve recorriendo la lista.
 *
 * Sólo la primera etiqueta —`en` de `en-US`—: la variante regional decide cómo
 * se escribe una fecha, no en qué idioma está la interfaz.
 */
export function preferredLanguage(requested, fallback = DEFAULT_LANGUAGE) {
    for (const tag of requested ?? []) {
        const base = tag.trim().toLowerCase().split('-')[0];
        if (isLanguage(base))
            return base;
    }
    return fallback;
}
/**
 * Lo mismo para una cabecera `Accept-Language`, que es como llega en el
 * servidor: `es-MX,es;q=0.9,en;q=0.8`.
 *
 * Se respeta el peso `q` porque el navegador lo manda ordenado por preferencia
 * real y no por el orden de la cadena; ignorarlo elegiría mal en cuanto alguien
 * tenga tres idiomas configurados.
 */
export function languageFromHeader(header, fallback = DEFAULT_LANGUAGE) {
    if (!header)
        return fallback;
    const tags = header
        .split(',')
        .map((part) => {
        const [tag, ...params] = part.trim().split(';');
        const quality = params
            .map((param) => /^q=([\d.]+)$/.exec(param.trim())?.[1])
            .find((value) => value !== undefined);
        return { tag: tag ?? '', quality: quality === undefined ? 1 : Number.parseFloat(quality) };
    })
        .filter((entry) => entry.tag && Number.isFinite(entry.quality) && entry.quality > 0)
        .sort((a, b) => b.quality - a.quality)
        .map((entry) => entry.tag);
    return preferredLanguage(tags, fallback);
}
//# sourceMappingURL=language.js.map